import { useMemo, useRef } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  agentKnowledgeBasesApi,
  knowledgeBasesApi,
  knowledgeDocumentsApi,
} from '@/services/api';
import { KNOWLEDGE_STATUS_POLL_MS } from '@/config';
import { useCollaborationConfig } from '@/hooks/useCollaborationConfigs';
import type { AssistantAvailableKnowledgeBase } from '@/types/assistant';
import type { AssistantTargetAgentKnowledge } from '@/utils/assistantTargetSummary';
import type {
  CreateKnowledgeBaseInput,
  CreateKnowledgeDocumentInput,
  KnowledgeBase,
  KnowledgeBaseFilters,
  KnowledgeDocument,
  KnowledgeDocumentFilters,
  UpdateKnowledgeBaseInput,
  UpdateKnowledgeDocumentInput,
} from '@/types';

/**
 * How many bases the membership index reads in one page. Bases are a small,
 * human-curated set; a page this size means the Documents library's
 * "which bases is this in?" column is one round of reads, not pagination.
 */
const MEMBERSHIP_INDEX_BASE_LIMIT = 100;

/**
 * A document is still settling while ingestion has not reached a terminal
 * state. Exported because both the list poll and the detail poll decide on it.
 */
export const isKnowledgeDocumentSettling = (document: Pick<KnowledgeDocument, 'status'>): boolean =>
  document.status === 'pending' || document.status === 'indexing';

/** True when any document in the page is still being ingested. */
export const hasSettlingKnowledgeDocuments = (
  documents: KnowledgeDocument[] | undefined
): boolean => (documents ?? []).some(isKnowledgeDocumentSettling);

// ============================================================================
// Knowledge documents (the account library — the primary surface)
// ============================================================================

/**
 * Documents for the account, newest work first.
 *
 * Ingestion runs on a background worker, so rows arrive `pending` and settle
 * later. This uses the app's existing status-refresh pattern — a React Query
 * `refetchInterval` — and stops the timer as soon as every row is `ready` or
 * `failed`, so an idle library makes no requests.
 */
export const useKnowledgeDocuments = (
  page = 1,
  limit = 20,
  filters?: KnowledgeDocumentFilters
) => {
  return useQuery({
    queryKey: ['knowledge-documents', page, limit, filters],
    queryFn: () => knowledgeDocumentsApi.getAll(page, limit, filters).then((res) => res.data),
    refetchInterval: (query) =>
      hasSettlingKnowledgeDocuments(query.state.data?.data)
        ? KNOWLEDGE_STATUS_POLL_MS
        : false,
  });
};

export const useKnowledgeDocument = (id: string | undefined) => {
  return useQuery({
    queryKey: ['knowledge-document', id],
    queryFn: () => knowledgeDocumentsApi.getById(id!).then((res) => res.data.data),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data && isKnowledgeDocumentSettling(query.state.data)
        ? KNOWLEDGE_STATUS_POLL_MS
        : false,
  });
};

/**
 * The document's original file as bytes, for the document page's viewer
 * (task 1081). Fetched through the authenticated API client as a Blob and
 * converted to an ArrayBuffer, which every viewer takes. One attempt only: a
 * storage failure should reach the page's inline error (with Download still
 * offered) promptly rather than after a run of retries.
 */
export const useKnowledgeDocumentFile = (
  id: string
): { data: ArrayBuffer | null; isLoading: boolean; error: Error | null } => {
  const query = useQuery({
    queryKey: ['knowledge-document-file', id],
    queryFn: () => knowledgeDocumentsApi.getFile(id).then((blob) => blob.arrayBuffer()),
    enabled: !!id,
    retry: false,
  });
  return { data: query.data ?? null, isLoading: query.isLoading, error: query.error };
};

export const useCreateKnowledgeDocumentFromText = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateKnowledgeDocumentInput) =>
      knowledgeDocumentsApi.createFromText(data).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
    },
  });
};

export const useUploadKnowledgeDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      name,
      description,
    }: {
      file: File;
      name: string;
      description?: string;
    }) => knowledgeDocumentsApi.upload(file, { name, description }).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
    },
  });
};

export const useUpdateKnowledgeDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKnowledgeDocumentInput }) =>
      knowledgeDocumentsApi.update(id, data).then((res) => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-document', variables.id] });
    },
  });
};

/**
 * Deleting a document removes it everywhere — its chunks and every membership
 * go with it — so base membership lists are invalidated alongside the library.
 */
export const useDeleteKnowledgeDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeDocumentsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-base-documents'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
};

// ============================================================================
// Knowledge bases (groupings)
// ============================================================================

export const useKnowledgeBases = (page = 1, limit = 20, filters?: KnowledgeBaseFilters) => {
  return useQuery({
    queryKey: ['knowledge-bases', page, limit, filters],
    queryFn: () => knowledgeBasesApi.getAll(page, limit, filters).then((res) => res.data),
  });
};

/**
 * The account's knowledge bases reduced to what the Assistant may see (task
 * 1079): name, description and document count, nothing else.
 *
 * The Composer guide holds no tools, so this list is the only way it learns
 * which bases the account has. The mapping here is the guard that keeps ids and
 * document names off the wire: the turn cannot carry a field this function does
 * not build.
 *
 * Failure is not an error state for the caller. A refused or failed list simply
 * yields none, and the guide falls back to "no bases available", which is the
 * same absent-field behaviour as connections.
 */
export const useKnowledgeBaseSummaries = () => {
  const query = useQuery({
    queryKey: ['knowledge-bases', 1, 100, undefined],
    queryFn: () => knowledgeBasesApi.getAll(1, 100).then((res) => res.data),
    // The sidebar asks on every turn; the list changes when someone visits the
    // Knowledge Bases page, not between messages.
    staleTime: 5 * 60_000,
    retry: false,
  });

  const knowledgeBases: AssistantAvailableKnowledgeBase[] = (query.data?.data ?? []).map(
    (base) => ({
      name: base.name,
      ...(base.description ? { description: base.description } : {}),
      ...(typeof base.documentCount === 'number' ? { documentCount: base.documentCount } : {}),
    })
  );

  return { knowledgeBases, isResolved: query.isSuccess };
};

export const useKnowledgeBase = (id: string | undefined) => {
  return useQuery({
    queryKey: ['knowledge-base', id],
    queryFn: () => knowledgeBasesApi.getById(id!).then((res) => res.data.data),
    enabled: !!id,
  });
};

export const useCreateKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateKnowledgeBaseInput) =>
      knowledgeBasesApi.create(data).then((res) => res.data.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
};

export const useUpdateKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKnowledgeBaseInput }) =>
      knowledgeBasesApi.update(id, data).then((res) => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', variables.id] });
    },
  });
};

/**
 * Deleting a base removes the grouping only — documents and their chunks
 * survive — so the document library is NOT invalidated as though rows had
 * disappeared; only base-shaped queries are.
 */
export const useDeleteKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeBasesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-base-documents'] });
    },
  });
};

// ============================================================================
// Base ↔ document membership
// ============================================================================

export const useKnowledgeBaseDocuments = (knowledgeBaseId: string | undefined) => {
  return useQuery({
    queryKey: ['knowledge-base-documents', knowledgeBaseId],
    queryFn: () =>
      knowledgeBasesApi.listDocuments(knowledgeBaseId!).then((res) => res.data.data),
    enabled: !!knowledgeBaseId,
  });
};

export const useAddKnowledgeBaseDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      knowledgeBaseId,
      documentId,
    }: {
      knowledgeBaseId: string;
      documentId: string;
    }) => knowledgeBasesApi.addDocument(knowledgeBaseId, documentId).then((res) => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['knowledge-base-documents', variables.knowledgeBaseId],
      });
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', variables.knowledgeBaseId] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
};

export const useRemoveKnowledgeBaseDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      knowledgeBaseId,
      documentId,
    }: {
      knowledgeBaseId: string;
      documentId: string;
    }) => knowledgeBasesApi.removeDocument(knowledgeBaseId, documentId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['knowledge-base-documents', variables.knowledgeBaseId],
      });
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', variables.knowledgeBaseId] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] });
    },
  });
};

/**
 * Which bases each document belongs to, for the Documents library.
 *
 * There is no per-document membership endpoint — membership is read from the
 * base side — so this reads every base's membership list once and inverts it
 * into a `documentId → KnowledgeBase[]` index. The reads are ordinary cached
 * queries keyed the same way `useKnowledgeBaseDocuments` keys them, so adding
 * or removing a membership anywhere refreshes this index for free.
 */
export const useKnowledgeMembershipIndex = () => {
  const basesQuery = useKnowledgeBases(1, MEMBERSHIP_INDEX_BASE_LIMIT);
  const bases = useMemo<KnowledgeBase[]>(() => basesQuery.data?.data ?? [], [basesQuery.data]);

  const membershipQueries = useQueries({
    queries: bases.map((base) => ({
      queryKey: ['knowledge-base-documents', base.id],
      queryFn: () => knowledgeBasesApi.listDocuments(base.id).then((res) => res.data.data),
    })),
  });

  const isLoading = basesQuery.isLoading || membershipQueries.some((query) => query.isLoading);

  // Cheap enough to rebuild each render (a handful of bases × their members),
  // and rebuilding avoids memoizing on a value React Query recreates anyway.
  const index = new Map<string, KnowledgeBase[]>();
  bases.forEach((base, position) => {
    (membershipQueries[position]?.data ?? []).forEach((membership) => {
      index.set(membership.documentId, [...(index.get(membership.documentId) ?? []), base]);
    });
  });

  return { bases, index, isLoading };
};

// ============================================================================
// Agent ↔ knowledge-base attachment (task 573)
// ============================================================================

export const useAgentKnowledgeBases = (agentId: string | undefined) => {
  return useQuery({
    queryKey: ['agent-knowledge-bases', agentId],
    queryFn: () => agentKnowledgeBasesApi.list(agentId!).then((res) => res.data.data),
    enabled: !!agentId,
  });
};

/**
 * The Agents of an AI Service's collaboration and the knowledge bases attached
 * to each, by name (task 1079) — what the modify guide reads off `targetSummary`
 * to describe what the service answers from today.
 *
 * Agents come from the config's definition nodes (their node name is the name
 * the User sees on the graph); each Agent's bases come from the same list the
 * Agent page uses, so on a service page these are usually cache hits. Returns
 * `undefined` until the config and EVERY Agent's list have loaded, and on any
 * failure: a partial list would tell the guide an Agent has no bases when it
 * simply was not read.
 */
export const useTargetAgentKnowledge = (
  collaborationConfigId: string | null | undefined
): AssistantTargetAgentKnowledge[] | undefined => {
  const configQuery = useCollaborationConfig(collaborationConfigId ?? undefined);
  const agentNodes = useMemo(() => {
    const nodes = (configQuery.data?.definition as { nodes?: unknown } | undefined)?.nodes;
    if (!Array.isArray(nodes)) return [];
    return nodes
      .map((node) => node as { type?: unknown; agentId?: unknown; name?: unknown; id?: unknown })
      .filter((node) => node.type === 'agent' && typeof node.agentId === 'string')
      .map((node) => ({
        agentId: node.agentId as string,
        name: typeof node.name === 'string' ? node.name : String(node.id ?? ''),
      }));
  }, [configQuery.data]);

  const baseQueries = useQueries({
    queries: agentNodes.map((node) => ({
      queryKey: ['agent-knowledge-bases', node.agentId],
      queryFn: () => agentKnowledgeBasesApi.list(node.agentId).then((res) => res.data.data),
      retry: false,
    })),
  });

  const result =
    !configQuery.isSuccess || baseQueries.some((query) => !query.isSuccess)
      ? undefined
      : agentNodes.map((node, position) => ({
          name: node.name,
          knowledgeBaseNames: (baseQueries[position]?.data ?? [])
            .map((link) => link.knowledgeBase?.name)
            .filter((name): name is string => typeof name === 'string'),
        }));

  // Same value, same reference: the provider's send callback depends on this,
  // and a fresh array every render would re-create it on every render.
  const serialized = JSON.stringify(result ?? null);
  const stable = useRef<{ key: string; value: AssistantTargetAgentKnowledge[] | undefined }>({
    key: serialized,
    value: result,
  });
  if (stable.current.key !== serialized) stable.current = { key: serialized, value: result };
  return stable.current.value;
};

export const useAddAgentKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, knowledgeBaseId }: { agentId: string; knowledgeBaseId: string }) =>
      agentKnowledgeBasesApi.add(agentId, knowledgeBaseId).then((res) => res.data.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-knowledge-bases', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', variables.agentId] });
    },
  });
};

export const useRemoveAgentKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, knowledgeBaseId }: { agentId: string; knowledgeBaseId: string }) =>
      agentKnowledgeBasesApi.remove(agentId, knowledgeBaseId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['agent-knowledge-bases', variables.agentId] });
      queryClient.invalidateQueries({ queryKey: ['agent', variables.agentId] });
    },
  });
};
